import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";

/** Re-trigger animations whenever you enter/leave a section (no reload needed) */
function useInViewRepeat<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false);

  // Track small-screen state and force `inView` on small devices so animations run without relying
  // on strict intersection thresholds (many mobile viewports are short and may never meet thresholds).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(max-width: 768px)");
    const handleMatch = () => {
      setIsSmallScreen(m.matches);
      if (m.matches) {
        setInView(true); // ensure visible on mobile
      } else {
        setInView(false); // let IntersectionObserver control on larger screens
      }
    };
    handleMatch();
    if (m.addEventListener) m.addEventListener("change", handleMatch);
    else m.addListener(handleMatch);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", handleMatch);
      else m.removeListener(handleMatch);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If small screen, we've already forced `inView` to true above; skip observer to avoid flicker.
    if (isSmallScreen) return;

    const observerOptions = { threshold: 0.18, rootMargin: "0px 0px -16% 0px", ...(options || {}) };
    const obs = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), observerOptions);

    obs.observe(el);
    return () => obs.disconnect();
  }, [isSmallScreen, options?.root, options?.rootMargin, options?.threshold]);

  return { ref, inView, isSmallScreen };
}

const easeOut = [0.16, 1, 0.3, 1] as const;

export default function SSLCertificatePage() {
  const reduceMotion = useReducedMotion();

  /** -------- Section observers (replay on every visit) -------- */
  const hero = useInViewRepeat<HTMLElement>({ threshold: 0.25 });
  const plans = useInViewRepeat<HTMLElement>({ threshold: 0.18 });
  const benefits = useInViewRepeat<HTMLElement>({ threshold: 0.18 });

  // Consider device small if any section reports small-screen. Use simpler animations on this flag.
  const isMobile = hero.isSmallScreen || plans.isSmallScreen || benefits.isSmallScreen;

  /** -------- Variants (different animations for different items) -------- */
  const vFadeUp = {
    hidden: { opacity: 0, y: 26 },
    show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: easeOut } },
  };

  const vSlideLeft = {
    hidden: { opacity: 0, x: 30 },
    show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: easeOut } },
  };

  const vSlideRight = {
    hidden: { opacity: 0, x: -30 },
    show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: easeOut } },
  };

  const vScaleIn = {
    hidden: { opacity: 0, scale: 0.92 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.75, ease: easeOut } },
  };

  const vStagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
  };

  const vCard = (i: number) => ({
    hidden: { opacity: 0, y: 18, scale: 0.985 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.65, ease: easeOut, delay: reduceMotion ? 0 : i * 0.04 },
    },
  });

  const vTitleSplit = {
    hidden: { opacity: 0, y: 18, filter: "blur(6px)" },
    show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: easeOut } },
  };

  // Mobile-friendly (lighter) variants to avoid heavy transforms and stagger on small devices
  const vFadeUpMobile = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: easeOut } },
  };

  const vSlideLeftMobile = {
    hidden: { opacity: 0, x: 8 },
    show: { opacity: 1, x: 0, transition: { duration: 0.42, ease: easeOut } },
  };

  const vSlideRightMobile = {
    hidden: { opacity: 0, x: -8 },
    show: { opacity: 1, x: 0, transition: { duration: 0.42, ease: easeOut } },
  };

  const vScaleInMobile = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.42, ease: easeOut } },
  };

  // No stagger on mobile for simplicity/performance
  const vStaggerMobile = { hidden: {}, show: {} };

  const vCardMobile = (i: number) => ({
    hidden: { opacity: 0, y: 8, scale: 1 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: easeOut } },
  });

  const vTitleMobile = {
    hidden: { opacity: 0, y: 8, filter: "blur(2px)" },
    show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.45, ease: easeOut } },
  };

  // Helper: pick a mobile variant when on small screens
  const chooseVariant = (desktop: any, mobile: any) => (isMobile ? mobile : desktop);


  const plansData = useMemo(
    () => [
      {
        tag: "Domain Validation",
        tagStyle: "bg-gray-100 text-gray-700",
        logo: "/pro-comodo.svg",
        logoAlt: "COMODO",
        badge: { text: "Most Popular", style: "bg-green-100 text-green-700" },
        title: "PositiveSSL",
        desc: "Basic encryption for personal websites & blogs",
        price: "₹ 899",
        border: "border-gray-200 hover:border-blue-300 hover:shadow-blue-100",
        ctaHover: "group-hover:from-blue-600 group-hover:to-cyan-500",
        listDot: "bg-green-100 text-green-600",
        features: ["Issued in minutes", "₹10,000 Warranty", "99.3% Browser Recognition", "Unlimited Reissues"],
        cta: "Get Started",
        featured: false,
      },
      {
        tag: "Best Value",
        tagStyle: "bg-gradient-to-r from-blue-600 to-cyan-500 text-white",
        logo: "/pro-rapidssl.svg",
        logoAlt: "RapidSSL",
        badge: { text: "Recommended", style: "bg-blue-100 text-blue-700" },
        title: "RapidSSL",
        desc: "Fast validation for small businesses",
        price: "₹ 553",
        border: "border-blue-200 shadow-2xl shadow-blue-100",
        ctaHover: "",
        listDot: "bg-blue-100 text-blue-600",
        features: ["Issued in just minutes", "₹10,000 Warranty", "99% Browser Ubiquity", "256-bit SSL Encryption"],
        cta: "Get This Plan",
        featured: true,
      },
      {
        tag: "Organization Validation",
        tagStyle: "bg-purple-100 text-purple-700",
        logo: "/pro-geotrust.svg",
        logoAlt: "GeoTrust",
        badge: { text: "Premium", style: "bg-purple-100 text-purple-700" },
        title: "QuickSSL Premium",
        desc: "For e-commerce & business websites",
        price: "₹ 2,543",
        border: "border-gray-200 hover:border-purple-300 hover:shadow-purple-100",
        ctaHover: "group-hover:from-purple-600 group-hover:to-pink-500",
        listDot: "bg-purple-100 text-purple-600",
        features: ["Issued in Minutes", "₹5,00,000 Warranty", "Unlimited Server License", "99.9% Browser Trust"],
        cta: "Get Premium",
        featured: false,
      },
    ],
    []
  );

  const benefitsData = useMemo(
    () => [
      {
        icon: "🔒",
        title: "Data Encryption",
        desc: "256-bit military-grade encryption protects sensitive information between browsers and servers from interception.",
        ring: "hover:border-blue-500",
        grad: "from-blue-500 to-cyan-400",
      },
      {
        icon: "📈",
        title: "SEO Boost",
        desc: "Google prioritizes HTTPS websites in search rankings. SSL can improve your visibility and organic traffic by up to 30%.",
        ring: "hover:border-green-500",
        grad: "from-green-500 to-emerald-400",
      },
      {
        icon: "🏢",
        title: "Trust & Credibility",
        desc: "Display the padlock icon and HTTPS to show visitors your site is secure, increasing conversions and reducing bounce rates.",
        ring: "hover:border-purple-500",
        grad: "from-purple-500 to-pink-400",
      },
      {
        icon: "⚡",
        title: "HTTP/2 Enabled",
        desc: "Modern browsers require HTTPS for HTTP/2 protocol, which significantly improves website loading speed and performance.",
        ring: "hover:border-yellow-500",
        grad: "from-yellow-500 to-amber-400",
      },
      {
        icon: "🛡️",
        title: "PCI Compliance",
        desc: "Required for e-commerce sites to accept credit card payments securely. SSL is mandatory for PCI DSS compliance standards.",
        ring: "hover:border-red-500",
        grad: "from-red-500 to-pink-400",
      },
      {
        icon: "🔍",
        title: "Browser Compatibility",
        desc: "99.9% recognition across all modern browsers and devices, ensuring seamless user experience everywhere.",
        ring: "hover:border-indigo-500",
        grad: "from-indigo-500 to-purple-400",
      },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <Navigation />

      {/* ================= HERO (Dark) ================= */}
      <section
        ref={hero.ref}
        className="relative min-h-[70vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#040814] via-[#0a1026] to-[#040814] text-white py-20"
      >
        {/* Animated BG (continuous) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"
            animate={reduceMotion || hero.isSmallScreen ? {} : { y: [0, -14, 0], x: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"
            animate={reduceMotion || hero.isSmallScreen ? {} : { y: [0, 18, 0], x: [0, -10, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
            animate={reduceMotion || hero.isSmallScreen ? {} : { scale: [1, 1.08, 1], opacity: [0.65, 1, 0.65] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />

        {/* Content */}
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={chooseVariant(vStagger, vStaggerMobile)}
            initial="hidden"
            animate={hero.inView || hero.isSmallScreen ? "show" : "hidden"}
            className="max-w-3xl mx-auto text-center space-y-8"
          >
            {/* Chip - slideRight */}
            <motion.div
              variants={chooseVariant(vSlideRight, vSlideRightMobile)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/30 mx-auto w-fit"
            >
              <motion.span
                className="w-2 h-2 rounded-full bg-blue-400"
                animate={reduceMotion || isMobile ? {} : { scale: [1, 1.35, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              />
              <span className="text-sm font-medium">Secure Your Website Today</span>
            </motion.div>

            {/* Title - blur+lift */}
            <motion.h1 variants={chooseVariant(vTitleSplit, vTitleMobile)} className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Premium SSL Certificates for
              <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                {" "}
                Unbreakable Security
              </span>
            </motion.h1>

            {/* Para - fadeUp */}
            <motion.p variants={chooseVariant(vFadeUp, vFadeUpMobile)} className="text-lg text-gray-300 leading-relaxed">
              Get industry-leading SSL certificates that encrypt data, build trust, and boost your SEO rankings.
              Issued in minutes with 99.9% browser recognition and backed by trusted Certificate Authorities.
            </motion.p>

            {/* Buttons - separate animations */}
            <motion.div variants={chooseVariant(vScaleIn, vScaleInMobile)} className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <motion.a
                href="#plans"
                whileHover={reduceMotion || isMobile ? {} : { y: -2, scale: 1.02 }}
                whileTap={reduceMotion || isMobile ? {} : { scale: 0.98 }}
                className="group relative px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold text-center overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/30"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center justify-center gap-2">
                  Compare SSL Plans
                  <motion.svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    whileHover={reduceMotion || isMobile ? {} : { x: 4 }}
                    transition={{ duration: 0.25 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </motion.svg>
                </span>
              </motion.a>

              <motion.a
                href="#benefits"
                whileHover={reduceMotion || isMobile ? {} : { y: -2 }}
                whileTap={reduceMotion || isMobile ? {} : { scale: 0.98 }}
                className="px-8 py-4 rounded-xl border-2 border-blue-500/30 text-blue-300 font-semibold text-center hover:bg-blue-500/10 hover:border-blue-400 transition-all duration-300"
              >
                Why SSL Matters
              </motion.a>
            </motion.div>

            {/* Stats - stagger items */}
            <motion.div variants={vStagger} className="pt-8 flex flex-wrap justify-center gap-8">
              <motion.div variants={vSlideRight} className="flex items-center gap-3">
                <motion.div
                  className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"
                  whileHover={reduceMotion || isMobile ? {} : { rotate: -3, scale: 1.08 }}
                  transition={{ duration: 0.25 }}
                >
                  <span className="text-blue-300">✓</span>
                </motion.div>
                <div>
                  <div className="font-semibold">Issued in Minutes</div>
                  <div className="text-sm text-gray-400">Instant Validation</div>
                </div>
              </motion.div>

              <motion.div variants={vSlideLeft} className="flex items-center gap-3">
                <motion.div
                  className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center"
                  whileHover={reduceMotion || isMobile ? {} : { rotate: 3, scale: 1.08 }}
                  transition={{ duration: 0.25 }}
                >
                  <span className="text-green-300">🔒</span>
                </motion.div>
                <div>
                  <div className="font-semibold">256-bit Encryption</div>
                  <div className="text-sm text-gray-400">Military Grade</div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

{/* ================= PLANS ================= */}
<section
  ref={plans.ref}
  id="plans"
  className="py-20 bg-gradient-to-b from-white to-gray-50"
>
  <div className="container mx-auto px-4 sm:px-6 lg:px-8">

    {/* Header */}
    <div className="text-center max-w-3xl mx-auto mb-16">
      <motion.div
        variants={chooseVariant(vScaleIn, vScaleInMobile)}
        initial="hidden"
        animate={plans.inView || plans.isSmallScreen ? "show" : "hidden"}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-600 mb-4 mx-auto w-fit"
      >
        <span className="w-2 h-2 rounded-full bg-blue-500" />
        <span className="text-sm font-medium">SSL Certificate Plans</span>
      </motion.div>

      <motion.h2
        variants={chooseVariant(vFadeUp, vFadeUpMobile)}
        initial="hidden"
        animate={plans.inView || plans.isSmallScreen ? "show" : "hidden"}
        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
      >
        Choose Your{" "}
        <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
          Security
        </span>{" "}
        Level
      </motion.h2>

      <motion.p
        variants={chooseVariant(vSlideLeft, vSlideLeftMobile)}
        initial="hidden"
        animate={plans.inView || plans.isSmallScreen ? "show" : "hidden"}
        className="text-lg text-gray-600"
      >
        Select from our range of SSL certificates, each offering different levels
        of validation and protection to meet your specific security needs.
      </motion.p>
    </div>

    {/* Cards */}
    <motion.div
      variants={chooseVariant(vStagger, vStaggerMobile)}
      initial="hidden"
      animate={plans.inView || plans.isSmallScreen ? "show" : "hidden"}
      className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12"
    >
      {plansData.map((p, i) => (
        <motion.div
          key={p.title}
          variants={isMobile ? vCardMobile(i) : vCard(i)}
          whileHover={reduceMotion || isMobile ? {} : { y: -6 }}
          transition={{ duration: 0.25 }}
          className={`group relative rounded-2xl p-6 md:p-8 border-2 transition-all duration-300 hover:shadow-2xl ${
            p.featured
              ? "bg-gradient-to-b from-blue-50 to-white"
              : "bg-white"
          } ${p.border}`}
        >
          {/* Tag */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={
              plans.inView || plans.isSmallScreen
                ? { opacity: 1, y: 0, transition: { duration: 0.45, delay: 0.05 } }
                : { opacity: 0, y: -8 }
            }
            className="mb-4 md:absolute md:-top-3 md:left-1/2 md:-translate-x-1/2 md:mb-0"
          >
            <span className={`px-4 py-1 rounded-full text-sm font-medium ${p.tagStyle}`}>
              {p.tag}
            </span>
          </motion.div>

          {/* Top */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <motion.img
                src={p.logo}
                alt={p.logoAlt}
                className="h-8"
                initial={{ opacity: 0, x: -10 }}
                animate={
                  plans.inView || plans.isSmallScreen
                    ? { opacity: 1, x: 0, transition: { duration: 0.55 } }
                    : { opacity: 0, x: -10 }
                }
              />

              <motion.span
                className={`text-xs px-3 py-1 rounded-full font-medium ${p.badge.style}`}
                initial={{ opacity: 0, x: 10 }}
                animate={
                  plans.inView || plans.isSmallScreen
                    ? { opacity: 1, x: 0, transition: { duration: 0.55 } }
                    : { opacity: 0, x: 10 }
                }
              >
                {p.badge.text}
              </motion.span>
            </div>

            <motion.h3
              initial={{ opacity: 0, y: 8 }}
              animate={
                plans.inView || plans.isSmallScreen
                  ? { opacity: 1, y: 0, transition: { duration: 0.55 } }
                  : { opacity: 0, y: 8 }
              }
              className="text-xl font-bold text-gray-900 mb-2"
            >
              {p.title}
            </motion.h3>

            <motion.p
              initial={{ opacity: 0 }}
              animate={
                plans.inView || plans.isSmallScreen
                  ? { opacity: 1, transition: { duration: 0.6, delay: 0.1 } }
                  : { opacity: 0 }
              }
              className="text-gray-600 mb-6"
            >
              {p.desc}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={
                plans.inView || plans.isSmallScreen
                  ? { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.12 } }
                  : { opacity: 0, y: 10 }
              }
            >
              <div className="flex items-baseline mb-2">
                <span className="text-4xl font-bold text-gray-900">{p.price}</span>
                <span className="text-gray-500 ml-2">/year</span>
              </div>
              <div className="text-sm text-gray-500">Billed annually</div>
            </motion.div>
          </div>

          {/* Features */}
          <motion.ul
            variants={chooseVariant(vStagger, vStaggerMobile)}
            initial="hidden"
            animate={plans.inView || plans.isSmallScreen ? "show" : "hidden"}
            className="space-y-4 mb-8"
          >
            {p.features.map((t) => (
              <motion.li key={t} variants={vFadeUp} className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${p.listDot}`}>
                  <span className="text-xs">✓</span>
                </div>
                <span className="text-gray-700">{t}</span>
              </motion.li>
            ))}
          </motion.ul>

          {/* CTA */}
          <motion.a
            href="#"
            whileHover={reduceMotion || isMobile ? {} : { scale: 1.02 }}
            whileTap={reduceMotion || isMobile ? {} : { scale: 0.98 }}
            className={`block w-full py-3 px-6 rounded-xl text-white font-semibold text-center transition-all duration-300 ${
              p.featured
                ? "bg-gradient-to-r from-blue-600 to-cyan-500 hover:shadow-blue-500/50"
                : "bg-gradient-to-r from-gray-900 to-gray-800"
            }`}
          >
            {p.cta}
          </motion.a>

          {/* Glow */}
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            initial={{ opacity: 0 }}
            whileHover={reduceMotion || isMobile ? {} : { opacity: 1 }}
            transition={{ duration: 0.25 }}
            style={{
              background:
                "radial-gradient(700px circle at 30% 0%, rgba(59,130,246,0.12), transparent 45%), radial-gradient(650px circle at 80% 70%, rgba(6,182,212,0.08), transparent 55%)",
            }}
          />
        </motion.div>
      ))}
    </motion.div>

    {/* Footer note */}
    <motion.div
      variants={chooseVariant(vFadeUp, vFadeUpMobile)}
      initial="hidden"
      animate={plans.inView || plans.isSmallScreen ? "show" : "hidden"}
      className="mt-16 text-center"
    >
      <p className="text-gray-600">
        All plans include free installation support and 30-day money-back guarantee
      </p>
    </motion.div>

  </div>
</section>



      {/* ================= BENEFITS (Dark) ================= */}
      <section
        ref={benefits.ref}
        id="benefits"
        className="py-20 bg-gradient-to-b from-gray-900 to-[#040814] text-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              variants={chooseVariant(vSlideRight, vSlideRightMobile)}
              initial="hidden"
              animate={benefits.inView || benefits.isSmallScreen ? "show" : "hidden"}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/30 mb-4 mx-auto w-fit"
            >
              <motion.span
                className="w-2 h-2 rounded-full bg-blue-400"
                animate={reduceMotion || isMobile ? {} : { scale: [1, 1.35, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              />
              <span className="text-sm font-medium">Why SSL is Essential</span>
            </motion.div>

            <motion.h2
              variants={chooseVariant(vTitleSplit, vTitleMobile)}
              initial="hidden"
              animate={benefits.inView || benefits.isSmallScreen ? "show" : "hidden"}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
            >
              More Than Just{" "}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">Encryption</span>
            </motion.h2>

            <motion.p
              variants={chooseVariant(vFadeUp, vFadeUpMobile)}
              initial="hidden"
              animate={benefits.inView || benefits.isSmallScreen ? "show" : "hidden"}
              className="text-lg text-gray-300"
            >
              SSL certificates provide comprehensive benefits that go beyond security, enhancing trust, credibility, and
              performance for your website.
            </motion.p>
          </div>

          {/* Grid */}
          <motion.div
            variants={chooseVariant(vStagger, vStaggerMobile)}
            initial="hidden"
            animate={benefits.inView || benefits.isSmallScreen ? "show" : "hidden"}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {benefitsData.map((b, i) => (
              <motion.div
                key={b.title}
                variants={isMobile ? vCardMobile(i) : vCard(i)}
                whileHover={reduceMotion || isMobile ? {} : { y: -6, scale: 1.01 }}
                transition={{ duration: 0.25 }}
                className={`group relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 md:p-8 border border-gray-700 ${b.ring} transition-all duration-300`}
              >
                {/* Icon */}
                <motion.div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-br ${b.grad} flex items-center justify-center mb-6`}
                  whileHover={reduceMotion || isMobile ? {} : { rotate: -4, scale: 1.07 }}
                  transition={{ duration: 0.25 }}
                >
                  <span className="text-2xl">{b.icon}</span>
                </motion.div>

                {/* Title */}
                <motion.h3
                  initial={{ opacity: 0, y: 8 }}
                  animate={benefits.inView || benefits.isSmallScreen ? { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.05 } } : { opacity: 0, y: 8 }}
                  className="text-xl font-bold mb-4"
                >
                  {b.title}
                </motion.h3>

                {/* Desc */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={benefits.inView || benefits.isSmallScreen ? { opacity: 1, transition: { duration: 0.6, delay: 0.1 } } : { opacity: 0 }}
                  className="text-gray-400 mb-6"
                >
                  {b.desc}
                </motion.p>

                <div className="pt-6 border-t border-gray-700" />


                {/* hover glow */}
                <motion.div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  initial={{ opacity: 0 }}
                  whileHover={reduceMotion || isMobile ? {} : { opacity: 1 }}
                  transition={{ duration: 0.25 }}
                  style={{
                    background:
                      "radial-gradient(700px circle at 20% 0%, rgba(59,130,246,0.10), transparent 45%), radial-gradient(650px circle at 80% 60%, rgba(6,182,212,0.08), transparent 55%)",
                  }}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={chooseVariant(vScaleIn, vScaleInMobile)}
            initial="hidden"
            animate={benefits.inView || benefits.isSmallScreen ? "show" : "hidden"}
            className="mt-16 text-center"
          >
            <motion.a
              href="#plans"
              whileHover={reduceMotion || isMobile ? {} : { y: -2, scale: 1.02 }}
              whileTap={reduceMotion || isMobile ? {} : { scale: 0.98 }}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300"
            >
              <span>Get Your SSL Certificate Now</span>
              <motion.svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                animate={reduceMotion || isMobile ? {} : { y: [0, -2, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </motion.svg>
            </motion.a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
